import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

const Login = lazy(() => import('../pages/Login'));
const Home = lazy(() => import('../pages/Home'));
const Register = lazy(() => import('../pages/Register'));
const Jobs = lazy(() => import('../pages/Jobs'));
const JobDetails = lazy(() => import('../pages/JobDetails'));
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const JobsByDesignation = lazy(() => import('../pages/JobsByDesignation'));
const JobsByInstitutions = lazy(() => import('../pages/browse/JobsByInstitutions'));
const JobsByCategory = lazy(() => import('../pages/browse/JobsByCategory'));
const JobsByLocation = lazy(() => import('../pages/browse/JobsByLocation'));
const JobsBySkills = lazy(() => import('../pages/browse/JobsBySkills'));

const RecruiterHotline = lazy(() => import('../pages/employer/RecruiterHotline'));
const PostJobsPage = lazy(() => import('../pages/employer/PostJobs'));
// AccessDatabase page removed — it was a 19-line wrapper around the shared
// EmployerLanding template whose pitch was already made on Employer Services
// (the umbrella page), Recruiter Plans, and Recruiter Hotline. The URL is kept
// as a redirect rather than allowed to 404, so any existing inbound links,
// campaigns, or search results still land somewhere useful.
const RecruiterLoginPage = lazy(() => import('../pages/employer/RecruiterLogin'));
const EmployerServices = lazy(() => import('../pages/employer/EmployerServices'));
const RecruiterPlans = lazy(() => import('../pages/employer/RecruiterPlans'));

// const MainLayout = lazy(() => import('../layouts/MainLayout'));
// const AdminLayout = lazy(() => import('../layouts/AdminLayout'));
// const RecruiterLayout = lazy(() => import('../layouts/RecruiterLayout'));
// const JobSeekerLayout = lazy(() => import('../layouts/JobSeekerLayout'));

const JobSeekerDashboard = lazy(() => import('../pages/jobseeker/Dashboard'));
const JobSeekerProfile = lazy(() => import('../pages/jobseeker/Profile'));
const JobSeekerApplications = lazy(() => import('../pages/jobseeker/Applications'));
const JobSeekerPreferences = lazy(() => import('../pages/jobseeker/Preferences'));
const JobSeekerResumeBuilder = lazy(() => import('../pages/jobseeker/ResumeBuilder'));
const JobSeekerNotifications = lazy(() => import('../pages/jobseeker/Notifications'));
const JobSeekerSchools = lazy(() => import('../pages/jobseeker/Schools'));

const RecruiterDashboard = lazy(() => import('../pages/recruiter/Dashboard'));
const PostJob = lazy(() => import('../pages/recruiter/PostJob'));
const ManageJobs = lazy(() => import('../pages/recruiter/ManageJobs'));
const Applicants = lazy(() => import('../pages/recruiter/Applicants'));
const RecruiterCandidates = lazy(() => import('../pages/recruiter/Candidates'));
const RecruiterNotifications = lazy(() => import('../pages/recruiter/Notifications'));

const AdminDashboard = lazy(() => import('../pages/admin/Dashboard'));
const AdminAnalytics = lazy(() => import('../pages/admin/Analytics'));
const AdminJobs = lazy(() => import('../pages/admin/Jobs'));
const AdminClients = lazy(() => import('../pages/admin/Clients'));
const AdminInstitutes = lazy(() => import('../pages/admin/Institutes'));
const AdminBoards = lazy(() => import('../pages/admin/Boards'));
const AdminCandidates = lazy(() => import('../pages/admin/Candidates'));
const AdminReports = lazy(() => import('../pages/admin/Reports'));
const AdminNotifications = lazy(() => import('../pages/admin/Notifications'));
const AdminEmailTemplate = lazy(() => import('../pages/admin/communication/EmailTemplate'));
const AdminEmailTemplateCreate = lazy(() => import('../pages/admin/communication/EmailTemplateCreate'));
const AdminSchedule = lazy(() => import('../pages/admin/communication/Schedule'));
const AdminScheduleCreate = lazy(() => import('../pages/admin/communication/ScheduleCreate'));
const AdminPostJob = lazy(() => import('../pages/admin/PostJob'));
const AdminEditJob = lazy(() => import('../pages/admin/EditJob'));
const AdminHeroBanners = lazy(() => import('../pages/admin/HeroBanners'));
const AdminNewspaperExtraction = lazy(() => import('../pages/admin/newspaper/NewspaperExtraction'));
const AdminNewspaperReview = lazy(() => import('../pages/admin/newspaper/NewspaperReview'));

// Table UI preview. Public and unguarded on purpose: it makes no API calls and
// holds no real data, so it can be opened with the backend down and without a
// session — which is the whole point of it. Its own banner states that everything
// on it is generated.
const TablePreview = lazy(() => import('../pages/dev/TablePreview'));
// Same idea for the hero banner crop: the real preview sits behind an admin
// session, and "will this picture survive the crop" is worth being able to
// check without one.
const HeroPreviewSandbox = lazy(() => import('../pages/dev/HeroPreviewSandbox'));

const publicRoutes = [
    {path: '/', element: <Home />},
    {path: '/dev/table-preview', element: <TablePreview />},
    {path: '/dev/hero-preview', element: <HeroPreviewSandbox />},
    // {path: '/access-denied', element: <AccessDenied /> },
    {path: '/login', element: <Login />},
    {path: '/register', element: <Register />},
    {path: '/jobs', element: <Jobs />},
    {path: '/jobs/:id', element: <JobDetails />},
    {path: '/forgot-password', element: <ForgotPassword />},
    {path: '/jobs-by-designation', element: <JobsByDesignation />},
    // Browse All Jobs now redirects to /jobs?browse=all
    {path: '/jobs-by-institutions', element: <JobsByInstitutions />},
    {path: '/jobs-by-category', element: <JobsByCategory />},
    {path: '/jobs-by-location', element: <JobsByLocation />},
    {path: '/jobs-by-skills', element: <JobsBySkills />},
    {path: '/employer/recruiter-hotline', element: <RecruiterHotline />},
    {path: '/employer/post-jobs', element: <PostJobsPage />},
    {path: '/employer/access-database', element: <Navigate to="/employer/employer-services" replace />},
    {path: '/employer/recruiter-login', element: <RecruiterLoginPage />},
    {path: '/employer/employer-services', element: <EmployerServices />},
    {path: '/employer/recruiter-plans', element: <RecruiterPlans />},
];

const recruiterProtectedRoutes = [
    { path: '/recruiter/dashboard', element: <RecruiterDashboard />},
    { path: '/recruiter/manage-jobs/post-job', element: <PostJob />},
    { path: '/recruiter/manage-jobs/edit-job/:id', element: <PostJob />},
    { path: '/recruiter/manage-jobs', element: <ManageJobs />},
    { path: '/recruiter/applicants', element: <Applicants />},
    { path: '/recruiter/candidates', element: <RecruiterCandidates />},
    { path: '/recruiter/notifications', element: <RecruiterNotifications />}
];

const jobSeekerProtectedRoutes = [
    {path: '/jobseeker/dashboard', element: <JobSeekerDashboard />},
    {path: '/jobseeker/profile', element: <JobSeekerProfile />},
    {path: '/jobseeker/applications', element: <JobSeekerApplications />},
    {path: '/jobseeker/preferences', element: <JobSeekerPreferences />},
    {path: '/jobseeker/resume-builder', element: <JobSeekerResumeBuilder />},
    {path: '/jobseeker/notifications', element: <JobSeekerNotifications />},
    {path: '/jobseeker/schools', element: <JobSeekerSchools />},
];

const adminProtectedRoutes = [
    {path: '/admin/dashboard', element: <AdminDashboard />},
    {path: '/admin/analytics', element: <AdminAnalytics />},
    {path: '/admin/jobs', element: <AdminJobs />},
    {path: '/admin/clients', element: <AdminClients />},
    {path: '/admin/institutes', element: <AdminInstitutes />},
    {path: '/admin/boards', element: <AdminBoards />},
    {path: '/admin/newspaper', element: <AdminNewspaperExtraction />},
    {path: '/admin/newspaper/:id', element: <AdminNewspaperReview />},
    {path: '/admin/candidates', element: <AdminCandidates />},
    {path: '/admin/reports', element: <AdminReports />},
    {path: '/admin/communication/email-template', element: <AdminEmailTemplate />},
    {path: '/admin/communication/email-template/create', element: <AdminEmailTemplateCreate />},
    {path: '/admin/communication/email-template/edit/:id', element: <AdminEmailTemplateCreate />},
    {path: '/admin/communication/email-template/view/:id', element: <AdminEmailTemplateCreate />},
    {path: '/admin/communication/schedule', element: <AdminSchedule />},
    {path: '/admin/communication/schedule/create', element: <AdminScheduleCreate />},
    {path: '/admin/communication/schedule/edit/:id', element: <AdminScheduleCreate />},
    {path: '/admin/communication/schedule/view/:id', element: <AdminScheduleCreate />},
    {path: '/admin/notifications', element: <AdminNotifications />},
    {path: '/admin/jobs/post-job', element: <AdminPostJob />},
    // Mirrors the recruiter's /recruiter/manage-jobs/edit-job/:id and renders the
    // same form. An admin may edit any school's job; the API enforces that, not
    // this route.
    {path: '/admin/jobs/edit-job/:id', element: <AdminEditJob />},
    {path: '/admin/hero-banners', element: <AdminHeroBanners />},
];

export { adminProtectedRoutes, jobSeekerProtectedRoutes, recruiterProtectedRoutes, publicRoutes };